import React, { useState } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { StepContainer } from '../StepContainer';
import { LocationInput, PropertyTypeSelect } from '../../form';
import ConfirmDialog from '../../../ui/ConfirmDialog';
import { createProperty, findPropertyByAddress } from '../../../../services/propertyService';
import { consumeCredits } from '../../../../services/creditService';

interface DescriptionStepProps {
  data: any;
  onChange: (data: any) => void;
  onNext: () => void;
  onBack: () => void;
}

export const DescriptionStep: React.FC<DescriptionStepProps> = ({
 data = {
   location: { postcode: '', city: '', address: '' },
   propertyType: null,
   area: 0
 },
 onChange,
 onNext,
  onBack // This parameter is kept but not used
}) => {
  const { user } = useAuth();
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

   const handleNext = async () => {
   if (!user) return;
   
   try {
     setLoading(true);
     setError(null);

     if (!data?.location?.address || !data?.location?.postcode || !data?.propertyType) {
       setError('Veuillez remplir l\'adresse complète');
       return;
     }

     const existingProperty = await findPropertyByAddress(
       user.uid,
       data.location.address,
       data.location.postcode
     );

     if (existingProperty?.estimateId) {
       setError('Une estimation existe déjà pour ce bien.');
       return;
     }

     setShowConfirm(true);
   } catch (err) {
     console.error('Erreur:', err);
     setError('Une erreur est survenue');
   } finally {
     setLoading(false);
   }
 };

  const handleConfirm = async () => {
    if (!user || !data?.location) return;
    setShowConfirm(false);
    
    try {
      setLoading(true);
      const propertyId = await createProperty(user.uid, {
        userId: user.uid,
        address: data.location.address,
        type: data.propertyType,
        area: data.area || 0, // Ensure area has a default value
        postcode: data.location.postcode,
        city: data.location.city,
        createdAt: new Date().toISOString(),
        lastEstimateAt: null,
        estimateId: null
      });

      onChange({
        ...data,
        propertyId
      });

      onNext();
    } catch (err) {
      console.error('Erreur:', err);
      setError('Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <StepContainer
      title="Description détaillée du bien"
      description="Informations générales sur le bien à rénover"
      onNext={handleNext}
      // Remove onBack prop to disable the back button
    >
      <div className="space-y-8">
        <div>
          <h3 className="text-lg font-medium mb-4">Identification du bien</h3>
          <LocationInput
            value={data.location}
            onChange={(location) => onChange({ ...data, location })}
          />
          <PropertyTypeSelect
            value={data.propertyType}
            onChange={(type) => onChange({ ...data, propertyType: type })}
          />
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-lg">
            {error}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={showConfirm}
        title="Confirmation des informations"
        message={`Attention : ces informations ne pourront plus être modifiées. Un crédit sera définitivement attribué à cette adresse : ${data?.location?.address}. Confirmez-vous ?`}
        confirmLabel="Confirmer"
        cancelLabel="Annuler"
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirm(false)}
        variant="warning"
      />
    </StepContainer>
  );
};

export default DescriptionStep;